import { type BuildAndBuyDispatchType, useBnbSelector } from "../../../redux/store";
import { formatPrice } from "../../../../../../common-deprecated/Globalize";
import { calculatePriceInclLicenseFee, hideDiscountInfo } from "../../../../../../common-deprecated/priceUtils";
import { setFinanceOption } from "../../../../../../common-deprecated/redux/actions/UtilActions";
import { pslsFinanceResponseHasNoQuotes } from "../../../utils/typedUtils";
import type { FinanceOptionType } from "../../../../../../common-deprecated/types/CommonTypes";
import { type BuildAndBuyLabelType, getBnbOptionalLabel } from "../../../utils/labels";
import {
    getBnbPriceConfig,
    isFinanceCompareQuotesSet,
    showBnbMonthlyRates,
} from "../../../utils/settings/bnbFinanceSettingUtils";
import { trackFinanceToggle } from "../../../utils/tracking";
import { showPrice } from "../../../../../../common-deprecated/settings/utils/financeSettingUtilsCommon";
import { formatAEMCashPrice } from "../../../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { checkAndSetFinanceProductTradeInCompatibility } from "../../../utils/basketUtils";
import { setTradeInAemIncompatible } from "../../../../../../common-deprecated/features/tradein/redux/actions/TradeInAemExternalDataActions";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

type UseFinanceToggleType = {
    overrideCashLabelKey: BuildAndBuyLabelType | null;
    formattedCash: string;
    formattedMonthly: string;
    showToggles: boolean; // completely hides all toggles
    showCashPrice: boolean;
    showCashLoader: boolean;
    showMonthlyPrice: boolean; // hides actual price
    showMonthlyLoader: boolean; // shows a loader instead of price
    showMonthlyRateToggle: boolean; // should hide complete tab
    financeOption: FinanceOptionType;
    setCash: () => void;
    setMonthly: () => void;
};

const useFinanceToggle = (): UseFinanceToggleType => {
    const totalPrice = useBnbSelector((state) => state.basket.total);
    const totalMonthly = useBnbSelector((state) => state.finance.total);
    const car = useBnbSelector((state) => state.car);
    const finance = useBnbSelector((state) => state.finance);
    const externalPrice = useBnbSelector((state) => state.externalPrice);
    const financeOption = useBnbSelector((state) => state.utils.financeOption);
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);
    const step = useBnbSelector((state) => state.navigation.step);
    const tradeInAemExternalData = useBnbSelector((state) => state.tradeInAemExternalData);
    const hideDirectMonthlyTotalRetailer = useBnbSelector((state) => state.retailerSettings.hideDirectMonthlyTotal); // This is a temporary setting. See DR-1218
    const dispatch = useDispatch<BuildAndBuyDispatchType>();
    // For now we always display the full base cash price (but including license fees apparently).
    // Subtracting promotions, WLTP tax, tradein value... would require a lot of disclaimers
    // and extra information, for which there seems to be no place in current designs
    const { cash, discount, licenseFee } = totalPrice;
    const cultureName = commonSettings.culture.name;

    const priceConfig = getBnbPriceConfig(commonSettings, car.model.internalCode);
    const showPriceInTab = showPrice(commonSettings);
    const showMonthlyRateToggle =
        showBnbMonthlyRates(commonSettings, bnbSettings, car.model.internalCode, step, car) &&
        !finance.error &&
        (!pslsFinanceResponseHasNoQuotes(finance, bnbSettings) || finance.fetching) &&
        isFinanceCompareQuotesSet(bnbSettings.enableFinanceCompare, finance.quotes.finance);

    // Flexibility matrix configuration where the discounted price should be used everywhere instead of the cash no visual indication is active when configured as such
    const priceToUse = hideDiscountInfo(priceConfig) ? discount : cash;
    // Also add the licenseFee when applicable
    const priceInclLicenseFee = calculatePriceInclLicenseFee(priceToUse, priceConfig, licenseFee);

    let overrideCashLabelKey: BuildAndBuyLabelType | null = null;
    // If there is a difference it means that license fee has been applied, also update the label in the cash tab if this is the case!
    if (
        priceToUse !== priceInclLicenseFee &&
        !!getBnbOptionalLabel({ bnbSettings, commonSettings }, "T1.genericTotalPriceDisclaimer")
    ) {
        overrideCashLabelKey = "T1.genericTotalPriceDisclaimer";
    }

    const showMonthlyLoader = finance.fetching;
    const formattedMonthly = !finance.fetching ? formatPrice(totalMonthly, cultureName, true) : "";

    return {
        overrideCashLabelKey,
        formattedCash: formatAEMCashPrice(priceInclLicenseFee, cultureName, priceConfig),
        formattedMonthly,
        showToggles: !hideDirectMonthlyTotalRetailer && showPrice(commonSettings),
        showCashPrice: showPriceInTab && priceConfig.priceEnabled,
        showCashLoader: externalPrice.fetchingFunctions || externalPrice.fetchingValue,
        showMonthlyPrice: !bnbSettings.showMonthlyRateDisclaimerInline && showPriceInTab,
        showMonthlyLoader,
        showMonthlyRateToggle,
        financeOption: hideDirectMonthlyTotalRetailer ? "cash" : financeOption,
        setCash: () => {
            dispatch(setFinanceOption("cash", false));
            if (tradeInAemExternalData.incompatible) dispatch(setTradeInAemIncompatible(false));
            trackFinanceToggle(step, "cash", "cc-action");
        },
        setMonthly: () => {
            dispatch(setFinanceOption("monthly", false));
            checkAndSetFinanceProductTradeInCompatibility(
                tradeInAemExternalData.data,
                !!tradeInAemExternalData.incompatible,
                "monthly",
                finance.info.productName,
                bnbSettings,
                dispatch,
            );
            trackFinanceToggle(step, "monthly", "cc-action");
        },
    };
};

export default useFinanceToggle;
